import { useState, useEffect, FormEvent, Fragment } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import LockResetIcon from "@mui/icons-material/LockReset";
import AssignmentIcon from "@mui/icons-material/Assignment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Container, Divider, IconButton, InputAdornment, Stack } from "@mui/material";

import format from "../utils/Role";
import InfoChip from "../utils/InfoChip";
import { useAuth } from "../auth/AuthProvider";
import SectionHeader from "../utils/SectionHeader";
import ProtectedRoute from "../auth/ProtectedRoute";
import { useConfgEditProfile } from "../../hooks/confg/base";

const Profile = () => {
  const { user } = useAuth();

  const [locked, setLocked] = useState(false);
  const [email, setEmail] = useState(user.email);
  const [lastName, setLastName] = useState(user.lastName);
  const [firstName, setFirstName] = useState(user.firstName);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [showPasswordConf, setShowPasswordConf] = useState(false);

  const profile = useConfgEditProfile();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    profile.mutate({
      lastName,
      firstName,
      newPassword,
      newPasswordConfirmation,
      currentPassword
    });
  };

  const cancelEditing = () => {
    setLocked(!locked);
    setEmail(user.email);
    setLastName(user.lastName);
    setFirstName(user.firstName);
  };

  const reset = () => {
    setEmail(user.email);
    setLastName(user.lastName);
    setFirstName(user.firstName);
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordConfirmation("");
  };

  const endAdornment = (onClick: () => void) => (
    <InputAdornment position="end">
      <IconButton edge="end" size="small" onClick={onClick} disabled={!locked} aria-label="toggle password visibility">
        {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
      </IconButton>
    </InputAdornment>
  );

  useEffect(() => {
    if (profile.isSuccess) {
      setLocked(false);
      reset();
    }
  }, [profile.isSuccess]);

  useEffect(() => {
    setEmail(user.email);
    setLastName(user.lastName);
    setFirstName(user.firstName);
  }, [user]);

  useEffect(() => {
    document.title = "[semiauth] | profile";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container component="main" maxWidth="sm">
        <SectionHeader
          title="Profile"
          icon={<AssignmentIcon />}
          buttons={[
            <Button key={"profile-edit-reset"} size="small" color="inherit" onClick={reset}>
              Reset
            </Button>
          ]}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: 1, color: "text.primary" }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="firstName"
                  name="firstName"
                  label="First name"
                  autoComplete="given-name"
                  value={firstName}
                  disabled={!locked}
                  onChange={(e: any) => setFirstName(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="lastName"
                  name="lastName"
                  label="Last name"
                  autoComplete="family-name"
                  value={lastName}
                  disabled={!locked}
                  onChange={(e: any) => setLastName(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="role"
                  name="role"
                  label="Role"
                  type="role"
                  autoComplete="new-role"
                  disabled
                  value={format(user.activeOrganization.role || "--")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  autoComplete="email"
                  label="Email address"
                  disabled
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {user.idp.includes("LOCAL") && (
                <Fragment>
                  <Grid item xs={12}>
                    <Divider textAlign="left">
                      <InfoChip
                        icon={<LockResetIcon />}
                        info="To reset account password, please enter all fields below"
                        label="Password reset"
                        placement="right"
                      />
                    </Divider>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="currentPassword"
                      name="currentPassword"
                      label="Current"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      disabled={!locked}
                      value={currentPassword}
                      onChange={(e: any) => setCurrentPassword(e.target.value)}
                      InputProps={{
                        endAdornment: endAdornment(() => setShowPassword(!showPassword))
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="newPassword"
                      name="newPassword"
                      label="New"
                      type={showPasswordNew ? "text" : "password"}
                      autoComplete="new-password"
                      value={newPassword}
                      disabled={!locked}
                      onChange={(e: any) => setNewPassword(e.target.value)}
                      InputProps={{
                        endAdornment: endAdornment(() => setShowPasswordNew(!showPasswordNew))
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="newPasswordConfirmation"
                      name="newPasswordConfirmation"
                      type={showPasswordConf ? "text" : "password"}
                      label="Confirmation"
                      autoComplete="new-password-confirmation"
                      disabled={!locked}
                      value={newPasswordConfirmation}
                      onChange={(e: any) => setNewPasswordConfirmation(e.target.value)}
                      InputProps={{
                        endAdornment: endAdornment(() => setShowPasswordConf(!showPasswordConf))
                      }}
                    />
                  </Grid>
                </Fragment>
              )}
            </Grid>

            <Stack direction="row" justifyContent="flex-end" alignItems="center">
              <Button variant={locked ? "outlined" : "contained"} onClick={cancelEditing} sx={{ mt: 3, mb: 2, mr: 2 }}>
                {locked ? "Cancel" : "Edit"}
              </Button>

              <Button type="submit" variant="contained" disabled={!locked} sx={{ mt: 3, mb: 2 }}>
                Save
              </Button>
            </Stack>
          </Box>
        </Box>
      </Container>
    </ProtectedRoute>
  );
};

export default Profile;
